import type { VisemeCue } from '../avatar/visemes.js';
import { ElevenLabsEngine, productionVoiceAvailable } from './elevenLabs.js';
import { textToCues } from './phonemes.js';
import { forSpeech } from './prosody.js';

/**
 * Motor de voz.
 *
 * Mismo patron que los adaptadores del modelo: una interfaz, varias
 * implementaciones. El avatar no sabe quien esta hablando.
 *
 *   webspeech    voz del propio navegador. No necesita credenciales ni cuesta
 *                dinero, asi que el sistema habla desde el primer dia.
 *   elevenlabs   voz de produccion, con marcas de tiempo por caracter.
 *                Entra cuando la universidad tenga la cuenta.
 *
 * El motor no solo emite audio: tambien va informando de que visema toca en
 * cada momento, para que la boca del avatar vaya con la voz.
 */

export interface SpeakHandlers {
  /**
   * Publica la linea de tiempo COMPLETA de la frase, con el instante (en el
   * reloj de performance.now) en el que empieza a sonar.
   *
   * Es lo que permite al avatar mezclar varios fonemas a la vez y anticipar el
   * siguiente. Si solo se le dijera "ahora toca este visema", no podria hacer
   * ni co-articulacion ni anticipacion: reaccionaria tarde y a saltos.
   * Se vuelve a llamar si el reloj se corrige sobre la marcha.
   */
  onTrack: (cues: VisemeCue[], t0: number) => void;
  /** Visema vigente. Solo lo usa el avatar ilustrado y el indicador de estado. */
  onViseme: (cue: VisemeCue['id']) => void;
  /** Volumen aproximado 0..1. */
  onAmplitude: (level: number) => void;
}

export interface VoiceEngine {
  readonly name: string;
  /** Voz concreta elegida, para diagnostico. null si el sistema no tiene ninguna. */
  voiceName(): string | null;
  /** Si el navegador puede usar este motor. */
  available(): boolean;
  /**
   * Desbloquea el audio. En iOS la primera reproduccion DEBE salir de un gesto
   * del usuario; si no, el navegador la descarta en silencio y no suena nada.
   */
  unlock(): void;
  /**
   * Opcional: empieza a preparar el audio de una frase que se dira despues,
   * mientras suena la anterior. Sin esto, cada frase esperaba su descarga al
   * llegarle el turno y entre frase y frase quedaba casi un segundo de silencio.
   */
  prefetch?(text: string): void;
  /** Habla una frase. Resuelve cuando termina de sonar. */
  speak(text: string, handlers: SpeakHandlers): Promise<void>;
  /** Corta de inmediato. */
  cancel(): void;
}

// ---------------------------------------------------------------------------
// Voz del navegador (Web Speech API)
// ---------------------------------------------------------------------------

/**
 * Voces en espanol ordenadas por naturalidad, la mejor primero.
 *
 * Las voces "neural"/"online" de Microsoft y las de Apple suenan a persona; las
 * antiguas (Jorge, Diego, Helena de escritorio) suenan claramente sinteticas.
 * Se prefiere espanol de America (es-MX, es-US, es-419) sobre el de Espana,
 * porque el publico es ecuatoriano.
 */
const VOICE_PREFERENCES: RegExp[] = [
  // 1. Voz de HOMBRE en espanol de America. El avatar es un hombre: una voz
  //    femenina sobre su cara resulta inmediatamente chocante.
  /Microsoft (Raul|Raúl)/i,
  /(Juan|Diego|Carlos).*\(?Spanish \(?(Mexico|United States|Argentina|Colombia)/i,
  // 2. Voz de hombre en espanol de Espana.
  /Microsoft (Pablo|Jorge)/i,
  /Jorge/i,
  // 3. Cualquier voz de servidor en espanol: suenan mejor que las locales.
  /Microsoft .*Online.*\(Spanish/i,
  /Google espa(ñ|n)ol de Estados Unidos/i,
  /Google espa(ñ|n)ol/i,
  // 4. Ultimo recurso: cualquier espanol, primero el de America.
  /es[-_](MX|US|419|CO|AR|EC|PE|CL)/i,
  /es[-_]ES/i,
];

function pickVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  const spanish = voices.filter((v) => v.lang.toLowerCase().startsWith('es'));
  if (spanish.length === 0) return null;

  for (const pattern of VOICE_PREFERENCES) {
    const hit = spanish.find((v) => pattern.test(v.name) || pattern.test(v.lang));
    if (hit) return hit;
  }
  // Las locales suelen sonar peor que las de servidor, pero es mejor que nada.
  return spanish.find((v) => !v.localService) ?? spanish[0] ?? null;
}

/** Las voces llegan de forma asincrona y la primera consulta suele venir vacia. */
function loadVoices(): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    const existing = window.speechSynthesis.getVoices();
    if (existing.length > 0) return resolve(existing);

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve(window.speechSynthesis.getVoices());
    };
    window.speechSynthesis.addEventListener('voiceschanged', done, { once: true });
    // Algunos navegadores no emiten el evento nunca: no nos quedamos colgados.
    window.setTimeout(done, 1200);
  });
}

class WebSpeechEngine implements VoiceEngine {
  readonly name = 'webspeech';
  voiceName(): string | null {
    return this.voice ? `${this.voice.name} (${this.voice.lang})` : null;
  }
  private voice: SpeechSynthesisVoice | null = null;
  private ready: Promise<void> | null = null;
  private current: SpeechSynthesisUtterance | null = null;
  private frame = 0;
  private keepAlive = 0;

  available(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  unlock(): void {
    if (!this.available()) return;
    // Un enunciado vacio dentro del gesto del usuario deja el canal abierto en
    // iOS. Sin esto, la primera respuesta del avatar sale muda.
    const primer = new SpeechSynthesisUtterance(' ');
    primer.volume = 0;
    primer.lang = 'es-ES';
    try {
      window.speechSynthesis.speak(primer);
    } catch {
      /* el navegador lo rechazo: se reintenta en el siguiente gesto */
    }
    this.ready ??= loadVoices().then((voices) => {
      this.voice = pickVoice(voices);
    });
  }

  async speak(text: string, handlers: SpeakHandlers): Promise<void> {
    if (!this.available()) return;
    this.ready ??= loadVoices().then((voices) => {
      this.voice = pickVoice(voices);
    });
    await this.ready;

    const spoken = forSpeech(text);
    if (!spoken.trim()) return;

    return new Promise<void>((resolve) => {
      const utterance = new SpeechSynthesisUtterance(spoken);
      if (this.voice) utterance.voice = this.voice;
      utterance.lang = this.voice?.lang ?? 'es-MX';

      // Un poco por debajo de 1 suena reposado y cercano; por encima suena
      // atropellado y mas sintetico. El tono neutro evita el efecto "robot".
      utterance.rate = 0.96;
      utterance.pitch = 1.0;
      utterance.volume = 1;

      // Linea de tiempo estimada a partir del texto...
      const cues = textToCues(spoken, spoken.length * 58);
      let startedAt = performance.now();
      // ...y el desfase que iremos corrigiendo con los eventos reales de habla.
      let offset = 0;
      let index = 0;
      let energy = 0.8;
      // Se pone a true en cuanto el motor da senales de vida.
      let progressed = false;
      // La pista NO se publica al encolar: el navegador tarda entre 200 y 500
      // ms en empezar a sonar, y en ese tiempo la boca ya iba por la segunda
      // silaba. Se publica en el instante real de arranque (onstart).
      let started = false;
      const begin = () => {
        if (started) return;
        started = true;
        startedAt = performance.now();
        offset = 0;
        handlers.onTrack(cues, startedAt);
      };

      const tick = () => {
        this.frame = requestAnimationFrame(tick);
        if (!started) return;
        const t = performance.now() - startedAt + offset;

        while (index < cues.length - 1 && cues[index + 1]!.at <= t) index += 1;
        const cue = cues[index];
        if (!cue) return;

        const done = t > cue.at + cue.duration + 60;
        handlers.onViseme(done ? 'REST' : cue.id);
      // Envolvente de volumen. NO puede ser un seno: un seno hace latir la boca
      // con un ritmo fijo, independiente de lo que se dice, y eso se percibe de
      // inmediato como algo mecanico. Sin audio real se usa un paseo aleatorio
      // lento, de modo que la variacion expresiva la aporten los visemas, que
      // si cambian con el texto.
        energy += (Math.random() - 0.5) * 0.09;
        energy = Math.min(1, Math.max(0.62, energy));
        handlers.onAmplitude(done ? 0.1 : energy);
      };

      /**
       * Correccion de deriva.
       *
       * El navegador avisa en que caracter va hablando. Comparamos esa posicion
       * real con la que habiamos estimado y ajustamos el reloj. Asi la boca no
       * se va desincronizando aunque la frase sea larga o la voz cambie de ritmo.
       */
      utterance.onstart = () => {
        progressed = true;
        begin();
      };

      utterance.onboundary = (event) => {
        progressed = true;
        begin();
        const charIndex = event.charIndex;
        if (typeof charIndex !== 'number') return;
        const target = cues.findIndex((_, i) => {
          const share = (i + 1) / cues.length;
          return share * spoken.length >= charIndex;
        });
        if (target < 0) return;
        const expected = cues[target]!.at;
        const actual = performance.now() - startedAt + offset;
        // Correccion suave: un salto brusco se ve como un tiron en la boca.
        offset += (expected - actual) * 0.35;
        // El reloj se ha movido: se republica para que la mezcla lo siga.
        handlers.onTrack(cues, startedAt - offset);
      };

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        cancelAnimationFrame(this.frame);
        window.clearInterval(this.keepAlive);
        window.clearTimeout(watchdog);
        handlers.onViseme('REST');
        handlers.onAmplitude(0);
        this.current = null;
        resolve();
      };

      const lastCue = cues[cues.length - 1];
      const estimated = lastCue ? lastCue.at + lastCue.duration : spoken.length * 58;

      /**
       * Perro guardian.
       *
       * Si el sistema no tiene ninguna voz utilizable, speak() no emite sonido y
       * tampoco dispara onend ni onerror: la promesa se quedaria esperando para
       * siempre y la cola de frases no avanzaria nunca, dejando al avatar
       * colgado en "Respondiendo...".
       */
      let watchdog = window.setTimeout(finish, estimated * 2 + 2500);

      /**
       * Deteccion temprana de "no hay voz".
       *
       * Si a los 400 ms el navegador no esta hablando ni tiene nada en cola y
       * no ha llegado ningun evento de progreso, la sintesis no ha arrancado
       * (tipico en servidores sin paquetes de idioma instalados). En ese caso NO
       * se corta de golpe: se deja vocalizar en silencio el tiempo estimado,
       * para que la boca acompane al texto en pantalla en vez de dar un tiron.
       */
      window.setTimeout(() => {
        if (settled) return;
        const s = window.speechSynthesis;
        if (!s.speaking && !s.pending && !progressed) {
          window.clearTimeout(watchdog);
          watchdog = window.setTimeout(finish, Math.max(0, estimated - 400));
          begin();
        }
      }, 400);

      utterance.onend = finish;
      utterance.onerror = finish;

      this.current = utterance;
      window.speechSynthesis.speak(utterance);
      this.frame = requestAnimationFrame(tick);

      // Chrome corta el habla pasados unos 15 segundos si no se le "toca".
      // Un pause/resume periodico lo mantiene vivo.
      this.keepAlive = window.setInterval(() => {
        if (!this.current) return;
        window.speechSynthesis.pause();
        window.speechSynthesis.resume();
      }, 9000);
    });
  }

  cancel(): void {
    cancelAnimationFrame(this.frame);
    window.clearInterval(this.keepAlive);
    this.current = null;
    if (this.available()) window.speechSynthesis.cancel();
  }
}

// ---------------------------------------------------------------------------

/** Motor mudo: el avatar sigue moviendo los labios pero no suena nada. */
class SilentEngine implements VoiceEngine {
  readonly name = 'silent';
  voiceName() {
    return null;
  }
  available() {
    return true;
  }
  unlock() {}
  async speak() {}
  cancel() {}
}

let fallback: VoiceEngine | null = null;

/**
 * Motor de respaldo: la voz del propio navegador.
 *
 * Se usa mientras no haya cuenta de voz configurada, y tambien como red de
 * seguridad si una frase concreta falla en produccion. El avatar nunca se queda
 * mudo por un fallo del proveedor.
 */
export function getVoiceEngine(): VoiceEngine {
  if (fallback) return fallback;
  const web = new WebSpeechEngine();
  fallback = web.available() ? web : new SilentEngine();
  return fallback;
}

/**
 * Elige el mejor motor disponible.
 *
 * Se prefiere la voz de produccion (ElevenLabs, con marcas de tiempo reales por
 * caracter); si el servidor no la tiene configurada, se sigue con la del
 * navegador. La decision la toma el servidor, no el navegador, porque es quien
 * sabe si hay credenciales.
 */
export async function resolveVoiceEngine(): Promise<VoiceEngine> {
  const production = new ElevenLabsEngine();
  if (production.available() && (await productionVoiceAvailable())) return production;
  return getVoiceEngine();
}
